import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { compareApi } from '@/api/endpoints'
import type { CompareVariantResult } from '@/api/endpoints'
import { buildWarehouseScopedStorageKey } from '@/features/warehouse/config'
import { useWarehouse } from '@/context/WarehouseContext'

export type CompareItem = {
  id: string
  variantId: number
  productId?: number
  sku: string
  title: string
  price: string
  priceBase?: number
  priceFinal: number
  priceDiscount?: number
  image?: string | null
  characteristics: { name: string; value: string }[]
}

type CompareContextValue = {
  items: CompareItem[]
  compareCount: number
  variantIds: number[]
  isInCompare: (variantId: number) => boolean
  addToCompare: (payload: {
    variantId: number
    productId?: number
    title?: string
    image?: string
  }) => void
  removeFromCompare: (variantId: number) => void
  toggleCompare: (payload: {
    variantId: number
    productId?: number
    title?: string
    image?: string
  }) => void
  clearCompare: () => void
  reloadFromServer: () => Promise<void>
}

const STORAGE_KEY = 'nurm_compare_variant_ids_v1'
const MAX_COMPARE = 4

const CompareContext = createContext<CompareContextValue | null>(null)

function loadVariantIdsFromStorage(storageKey: string): number[] {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((v: unknown) => typeof v === 'number' && Number.isInteger(v)).slice(0, MAX_COMPARE)
  } catch {
    return []
  }
}

function saveVariantIdsToStorage(storageKey: string, ids: number[]) {
  localStorage.setItem(storageKey, JSON.stringify(ids.slice(0, MAX_COMPARE)))
}

function formatPrice(value: number) {
  return new Intl.NumberFormat('ru-RU').format(value) + ' ₽'
}

function apiVariantToItem(
  v: CompareVariantResult,
  _displayCache?: { title?: string; image?: string }
): CompareItem {
  return {
    id: String(v.id),
    variantId: v.id,
    productId: v.product,
    sku: v.sku,
    title: v.product_name ?? `Вариант ${v.sku}`,
    price: formatPrice(v.price.final),
    priceBase: v.price.base,
    priceFinal: v.price.final,
    priceDiscount: v.price.discount,
    image: v.image ?? undefined,
    characteristics: v.characteristics ?? [],
  }
}

export const CompareProvider = ({ children }: { children: ReactNode }) => {
  const { selectedWarehouseId } = useWarehouse()
  const compareStorageKey = buildWarehouseScopedStorageKey(STORAGE_KEY, selectedWarehouseId)
  const [variantIds, setVariantIds] = useState<number[]>(() => loadVariantIdsFromStorage(compareStorageKey))
  const [items, setItems] = useState<CompareItem[]>([])
  const [displayCache, setDisplayCache] = useState<Record<number, { title?: string; image?: string }>>({})
  const [storageReadyKey, setStorageReadyKey] = useState<string | null>(compareStorageKey)
  const displayCacheRef = useRef(displayCache)
  displayCacheRef.current = displayCache

  useEffect(() => {
    setVariantIds(loadVariantIdsFromStorage(compareStorageKey))
    setItems([])
    setDisplayCache({})
    setStorageReadyKey(compareStorageKey)
  }, [compareStorageKey])

  const reloadFromServer = useCallback(async () => {
    if (variantIds.length === 0) {
      setItems([])
      return
    }
    try {
      const data = await compareApi.getVariants(variantIds)
      const cache = displayCacheRef.current
      setItems(data.map((v) => apiVariantToItem(v, cache[v.id])))
    } catch {
      setItems([])
    }
  }, [variantIds.join(',')])

  useEffect(() => {
    reloadFromServer()
  }, [reloadFromServer, storageReadyKey])

  useEffect(() => {
    if (storageReadyKey !== compareStorageKey) return
    saveVariantIdsToStorage(compareStorageKey, variantIds)
  }, [compareStorageKey, variantIds, storageReadyKey])

  const isInCompare = useCallback(
    (variantId: number) => variantIds.includes(variantId),
    [variantIds]
  )

  const addToCompare = useCallback(
    (payload: {
      variantId: number
      productId?: number
      title?: string
      image?: string
    }) => {
      const { variantId, title, image } = payload
      setVariantIds((prev) => {
        if (prev.includes(variantId)) return prev
        if (prev.length >= MAX_COMPARE) return prev
        return [...prev, variantId]
      })
      if (title !== undefined || image !== undefined) {
        setDisplayCache((c) => ({ ...c, [variantId]: { ...c[variantId], title, image } }))
      }
    },
    []
  )

  const removeFromCompare = useCallback((variantId: number) => {
    setVariantIds((prev) => prev.filter((id) => id !== variantId))
    setDisplayCache((c) => {
      const next = { ...c }
      delete next[variantId]
      return next
    })
  }, [])

  const toggleCompare = useCallback(
    (payload: {
      variantId: number
      productId?: number
      title?: string
      image?: string
    }) => {
      if (isInCompare(payload.variantId)) {
        removeFromCompare(payload.variantId)
      } else {
        addToCompare(payload)
      }
    },
    [isInCompare, addToCompare, removeFromCompare]
  )

  const clearCompare = useCallback(() => {
    setVariantIds([])
    setItems([])
    setDisplayCache({})
  }, [])

  const value = useMemo(
    () => ({
      items,
      compareCount: variantIds.length,
      variantIds,
      isInCompare,
      addToCompare,
      removeFromCompare,
      toggleCompare,
      clearCompare,
      reloadFromServer,
    }),
    [
      items,
      variantIds,
      isInCompare,
      addToCompare,
      removeFromCompare,
      toggleCompare,
      clearCompare,
      reloadFromServer,
    ]
  )

  return (
    <CompareContext.Provider value={value}>
      {children}
    </CompareContext.Provider>
  )
}

export const useCompare = () => {
  const ctx = useContext(CompareContext)
  if (!ctx) {
    throw new Error('useCompare must be used within CompareProvider')
  }
  return ctx
}
